import React, { useCallback, useEffect, useState } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../context/ThemeContext';
import { Typography } from '../components/ui';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { api } from '../services/api';
import {
  count,
  share,
  minutes,
  followerGrowth,
  livesImpressionsHint,
  productsHint,
  completionLabel,
  isEmptyReport,
} from './finance/masterAnalytics';
import { FINANCE_PERIODS, type FinancePeriod } from '../types/masterSales';
import type { MasterAnalyticsReport } from '../types/masterAnalytics';

import { ON_ACCENT_TEXT } from '../styles/accentText';
/**
 * CORE-1141 — «Аналитика» мастера: аудитория и контент.
 *
 * Соседний экран к «Продажам» и сознательно не они: там деньги, здесь штуки.
 * Ни одна сумма сюда не приходит и не считается — источник правды по деньгам
 * один, и он в финансовом разделе. Ссылка туда стоит на видном месте, чтобы
 * два раздела не выглядели двумя правдами об одном.
 *
 * Экран держит два различения, без которых числа выглядят так же, но означают
 * другое:
 *
 *   • ПРОЧЕРК ≠ НОЛЬ. Отписки не записывались до этой задачи, показы эфиров не
 *     несли мастера, витрина программы не отправляла событий вовсе. Ноль на их
 *     месте читается как «никто не отписался» и «никто не смотрел» — то есть
 *     врёт ровно там, где мастер собирается принимать решение. Поэтому рядом с
 *     каждым прочерком стоит причина, и приходит она с сервера.
 *
 *   • АУДИТОРИЯ — ЭТО СЧЁТЧИКИ, А НЕ ЛЮДИ. Ни одного имени зрителя или
 *     покупателя здесь нет и быть не может: их нет в ответе. Разрез «откуда
 *     пришли» появляется только там, где набралась достаточная выборка, иначе
 *     строка из одного человека вместе со временем показа его и называет.
 */

export default function MasterAnalyticsScreen() {
  const { colors: C } = useTheme();

  const [period, setPeriod] = useState<FinancePeriod>('30d');
  const [report, setReport] = useState<MasterAnalyticsReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async (p: FinancePeriod) => {
    setLoading(true);
    setFailed(false);
    try {
      setReport(await api.getMasterAnalytics({ period: p }));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(period); }, [load, period]);

  const empty = report != null && isEmptyReport(report);
  const impressionsHint = report ? livesImpressionsHint(report.lives) : '';
  const productHint = report ? productsHint(report.products) : '';

  const section = (title: string, children: React.ReactNode, hint?: string) => (
    <View style={[s.card, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}>
      <Typography variant="body" style={{ color: C.textPrimary, fontWeight: '700' }}>{title}</Typography>
      <View style={s.metricsRow}>{children}</View>
      {hint ? (
        <Typography variant="caption" style={{ color: C.textMuted }}>{hint}</Typography>
      ) : null}
    </View>
  );

  return (
    <View style={[s.root, { backgroundColor: C.pageBg }]}>
      <Header />
      <ScrollView style={s.scroll} contentContainerStyle={s.scrollContent}>
        <View style={s.content}>
          <Typography variant="h2" style={{ color: C.textPrimary }}>Аналитика</Typography>

          {/* Деньги — соседний раздел. Названо прямо, чтобы «Аналитика» и
              «Продажи» не читались как два ответа на один вопрос. */}
          <Typography variant="caption" style={{ color: C.textMuted }}>
            Аудитория и контент. Деньги, продажи и выплаты — в разделе «Финансы».
          </Typography>

          <View style={s.periods}>
            {FINANCE_PERIODS.map((p) => {
              const active = p.code === period;
              return (
                <TouchableOpacity
                  key={p.code}
                  onPress={() => setPeriod(p.code)}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  testID={`analyticsPeriod-${p.code}`}
                  style={[
                    s.period,
                    { borderColor: active ? C.solar : C.border, backgroundColor: active ? C.solar : 'transparent' },
                  ]}
                >
                  <Typography variant="caption" style={{ color: active ? ON_ACCENT_TEXT : C.textPrimary }}>
                    {p.label}
                  </Typography>
                </TouchableOpacity>
              );
            })}
          </View>

          {loading ? (
            <ActivityIndicator color={C.textMuted} style={{ marginTop: 32 }} />
          ) : failed || !report ? (
            <View style={s.empty}>
              <Typography variant="body" style={{ color: C.textSecondary }}>
                Не получилось загрузить аналитику.
              </Typography>
              <TouchableOpacity
                onPress={() => void load(period)}
                accessibilityRole="button"
                style={[s.retry, { borderColor: C.border }]}
              >
                <Typography variant="body" style={{ color: C.textPrimary }}>Попробовать снова</Typography>
              </TouchableOpacity>
            </View>
          ) : empty ? (
            <View style={s.empty}>
              <Ionicons name="stats-chart-outline" size={26} color={C.textMuted} />
              <Typography variant="body" style={{ color: C.textMuted, marginTop: 10, textAlign: 'center' }}>
                За этот период данных ещё нет.
              </Typography>
            </View>
          ) : (
            <>
              {section('Профиль', (
                <>
                  <Metric label="Открывали профиль" value={count(report.profile.views)} C={C} />
                  <Metric label="Разных людей" value={count(report.profile.uniqueViewers)} C={C} />
                  <Metric label="Подписчиков" value={count(report.profile.followersTotal)} C={C} />
                  {/* Прирост, а не приход: пока отписки не считаются, здесь так и написано. */}
                  <Metric label="Динамика" value={followerGrowth(report.profile)} C={C} />
                </>
              ))}

              {section('Практики', (
                <>
                  <Metric label="Открытий" value={count(report.practices.opens)} C={C} />
                  <Metric label="Просмотров начато" value={count(report.practices.starts)} C={C} />
                  <Metric
                    label="Досмотрели"
                    value={completionLabel(
                      report.practices.completions,
                      report.practices.starts,
                      report.practices.completionRate,
                    )}
                    C={C}
                  />
                  <Metric label="Разных зрителей" value={count(report.practices.uniqueViewers)} C={C} />
                </>
              ))}

              {report.practices.top.length > 0 && (
                <View style={[s.card, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}>
                  <Typography variant="body" style={{ color: C.textPrimary, fontWeight: '700' }}>
                    Что смотрят
                  </Typography>
                  {report.practices.top.map((row) => (
                    <View key={row.id} style={[s.row, { borderBottomColor: C.borderSubtle }]}>
                      <Typography variant="body" style={{ color: C.textPrimary, flex: 1 }} numberOfLines={1}>
                        {row.title}
                      </Typography>
                      <Typography variant="caption" style={{ color: C.textMuted }}>
                        {completionLabel(row.completions, row.starts, row.completionRate)}
                      </Typography>
                    </View>
                  ))}
                </View>
              )}

              {section('Live-сессии', (
                <>
                  <Metric label="Показов" value={count(report.lives.impressions)} C={C} />
                  <Metric label="Входов" value={count(report.lives.joins)} C={C} />
                  <Metric label="Разных участников" value={count(report.lives.uniqueParticipants)} C={C} />
                  <Metric label="Медиана присутствия" value={minutes(report.lives.medianMinutes)} C={C} />
                </>
              ), impressionsHint)}

              {report.products.rows.length > 0 && section('Платные продукты', (
                <>
                  <Metric label="Показов витрины" value={count(report.products.views)} C={C} />
                  <Metric label="Покупок" value={count(report.products.purchases)} C={C} />
                  <Metric label="Конверсия" value={share(report.products.conversionRate)} C={C} />
                </>
              ), productHint)}

              {report.acquisition.length > 0 && (
                <View style={[s.card, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}>
                  <Typography variant="body" style={{ color: C.textPrimary, fontWeight: '700' }}>
                    Откуда приходят
                  </Typography>
                  {report.acquisition.map((row) => (
                    <View key={`${row.source}-${row.campaign ?? ''}`} style={[s.row, { borderBottomColor: C.borderSubtle }]}>
                      <Typography variant="body" style={{ color: C.textPrimary, flex: 1 }} numberOfLines={1}>
                        {row.source}{row.campaign ? ` · ${row.campaign}` : ''}
                      </Typography>
                      <Typography variant="caption" style={{ color: C.textMuted }}>
                        {count(row.viewers)}
                      </Typography>
                    </View>
                  ))}
                </View>
              )}

              {report.notes.length > 0 && (
                <View style={[s.notice, { backgroundColor: C.backgroundSoft, borderColor: C.borderSubtle }]}>
                  <Ionicons name="information-circle-outline" size={16} color={C.textMuted} />
                  <View style={{ flex: 1, gap: 6 }}>
                    {/* Дословно с сервера: он один знает, чего в данных нет. */}
                    {report.notes.map((note, i) => (
                      <Typography key={i} variant="caption" style={{ color: C.textSecondary }}>{note}</Typography>
                    ))}
                  </View>
                </View>
              )}

              <Typography variant="caption" style={{ color: C.textMuted }}>
                {report.contract.privacy}
              </Typography>
              <Typography variant="caption" style={{ color: C.textMuted }}>
                {report.contract.money}
              </Typography>
            </>
          )}
        </View>
        {/* CORE-1743: подвал внутри прокрутки. Снаружи он забирал высоту окна, и на
            ноутбуке страница сжималась до нуля — виден один подвал, прокрутки нет. */}
        <Footer />
      </ScrollView>
    </View>
  );
}

const Metric: React.FC<{ label: string; value: string; C: any }> = ({ label, value, C }) => (
  <View style={{ gap: 2, minWidth: 128 }}>
    <Typography variant="caption" style={{ color: C.textMuted }}>{label}</Typography>
    <Typography variant="body" style={{ color: C.textPrimary, fontWeight: '700' }}>{value}</Typography>
  </View>
);

const s = StyleSheet.create({
  root: { flex: 1 },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  // Шапка — 90px: с отступом 60 заголовок уходил под неё.
  content: { paddingTop: 120, paddingBottom: 40, paddingHorizontal: 20, maxWidth: 760, alignSelf: 'center', width: '100%', gap: 12 },
  periods: { flexDirection: 'row', gap: 8, marginTop: 4 },
  period: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 100, borderWidth: 1 },
  card: { padding: 16, borderRadius: 16, borderWidth: 1, gap: 12 },
  metricsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 20 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderBottomWidth: 1 },
  notice: { flexDirection: 'row', alignItems: 'flex-start', gap: 8, padding: 12, borderRadius: 12, borderWidth: 1 },
  empty: { alignItems: 'center', paddingVertical: 40 },
  retry: { marginTop: 12, paddingHorizontal: 16, paddingVertical: 8, borderRadius: 100, borderWidth: 1 },
});
